import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { fetchAndParseContent, fetchInfoPage } from "../contenful/ContentfulActions";
import { ContentModelName, InfoPage } from "../contenful/types";
import { getContainer } from "../ioc/IOCSetup";
import { documentToReactComponentsLocal, richTextOptions } from "../ui/InfoPageView";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../RootReducer";
import { BrandLogo } from "../ui/navigation/NavMenu";

interface OptModalProps {
    show: boolean;
    handleUserLogin: () => void;
    hide: () => void;
}

const OptModal: React.FC<OptModalProps> = ({ show, hide, handleUserLogin }) => {
    const dispatch = useDispatch();
    const global = useSelector((app: AppState) => app.contentful.global);
    const lng = getContainer().getSettingsService().getActiveLocale().code;

    const [formData, setFormData] = useState({
        ageConfirmation: false,
        nonGovtAffiliation: false,
        nonGamingLicense: false,
        termsAndServices: false,
    });

    const [error, setError] = useState(false);
    const [infoPage, setInfoPage] = useState<InfoPage[]>([]);
    const [showInfo, setShowInfo] = useState(false);

    useEffect(() => {
        if (!global) {
            dispatch(fetchAndParseContent(ContentModelName.GLOBAL, lng) as any);
        }
    }, [dispatch, global, lng]);

    useEffect(() => {
        if (infoPage.length === 0) {
            fetchInfoPage("terms", lng)
                .then((pages: InfoPage[]) => setInfoPage(pages))
                .catch((err) => console.error("Error fetching info page", err));
        }
    }, [lng, infoPage]);



    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (Object.values(formData).every((value) => value)) {
            hide();
            handleUserLogin();
        } else {
            setError(true);
        }
    };


    const renderInfoPage = () => (
        <div className="container mt-4">
            <div className="context-info-opt">
                <div className="infoPage-container-outer">
                    <div className="infoPage-container">
                        {infoPage[0] && documentToReactComponentsLocal(infoPage[0].body, richTextOptions)}
                    </div>
                </div>
            </div>
            <div className="py-3">
                <button
                    style={{ width: "100%" }}
                    onClick={() => setShowInfo(false)}
                    className="btn btn-primary w-full"
                >
                    Back
                </button>
            </div>
        </div>
    );

    const renderConsentForm = () => (
        <div className="container mt-4">
            <form onSubmit={handleSubmit}>
                {[
                    {
                        id: "ageConfirmation",
                        label: "I confirm that I am 21 years of age or older.",
                    },
                    {
                        id: "nonGovtAffiliation",
                        label: "I confirm that I am not a government official or related employee.",
                    },
                    {
                        id: "nonGamingLicense",
                        label: "I confirm that I do not hold a gaming employment license.",
                    },
                    {
                        id: "termsAndServices",
                        label: (
                            <>
                                I confirm to terms and services{" "}
                                <span
                                    style={{
                                        color: "blue",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                    }}
                                    onClick={() => setShowInfo(true)}
                                >
                                    Terms and Services
                                </span>
                            </>
                        ),
                    },
                ].map(({ id, label }) => (
                    <div className="form-check mb-3" key={id}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id={id}
                            name={id}
                            checked={formData[id as keyof typeof formData]}
                            onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor={id}>
                            {label}
                        </label>
                    </div>
                ))}
                <div className="py-3">
                    <button style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }} type="submit" className="btn btn-primary w-full">
                        Submit
                    </button>
                    {error && (
                        <div className="alert alert-danger mt-3" role="alert">
                            Consent to terms to continue.
                        </div>
                    )}
                </div>
            </form>
        </div>
    );

    return (
        <div className="context-opt">
            <Modal
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                show={show}
                onHide={() => hide()}
            >
                <Modal.Header>
                    <div className="desktop-logo-container">
                        <div className="mb-4">{global && <BrandLogo global={global} />}</div>
                        {!showInfo && <h3>Consent for Usage</h3>}
                    </div>
                </Modal.Header>
                <Modal.Body>{showInfo ? renderInfoPage() : renderConsentForm()}</Modal.Body>
            </Modal>
        </div>
    );
};


export default OptModal;